'use client'

import { useEffect, useState, useRef, useCallback } from 'react'
import { ArrowUp, ArrowDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useDebouncedCallback } from 'use-debounce'

export function ScrollButtons() {
  const [showTop, setShowTop] = useState(false)
  const [showBottom, setShowBottom] = useState(false)
  const isAutoScrolling = useRef(false)

  const checkScroll = useCallback(() => {
    // If body scrolling is locked (e.g., modal open), hide buttons
    const bodyOverflow = window.getComputedStyle(document.body).overflow
    if (bodyOverflow === 'hidden') {
      setShowTop(false)
      setShowBottom(false)
      return
    }

    const scrollTop = window.scrollY
    const windowHeight = window.innerHeight
    const docHeight = document.documentElement.scrollHeight

    // Show top button if scrolled down more than 300px
    setShowTop(scrollTop > 300)

    // Show bottom button if not at the bottom (with some buffer)
    // And only if the page is actually scrollable (docHeight > windowHeight)
    setShowBottom(
      docHeight > windowHeight &&
      scrollTop + windowHeight < docHeight - 100
    )
  }, [])

  const handleScrollStop = useDebouncedCallback(() => {
    isAutoScrolling.current = false
    checkScroll()
  }, 100)

  useEffect(() => {
    // Set initial state on mount (client-side only)
    const rafId = requestAnimationFrame(() => checkScroll())

    const handleScroll = () => {
      if (isAutoScrolling.current) {
        handleScrollStop()
        return
      }
      checkScroll()
    }

    window.addEventListener('scroll', handleScroll)

    // Also listen for resize events as they might change scrollability
    window.addEventListener('resize', checkScroll)

    // Listen for body style changes (e.g., when modals lock scrolling)
    const observer = new MutationObserver(() => checkScroll())
    observer.observe(document.body, { attributes: true, attributeFilter: ['style', 'class'] })

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', checkScroll)
      observer.disconnect()
    }
  }, [checkScroll, handleScrollStop])

  const scrollToTop = () => {
    isAutoScrolling.current = true
    setShowTop(false) // Immediately hide the button we clicked
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const scrollToBottom = () => {
    isAutoScrolling.current = true
    setShowBottom(false) // Immediately hide the button we clicked
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
  }

  if (!showTop && !showBottom) return null

  return (
    <div className="fixed bottom-8 right-8 z-50 flex flex-col gap-2 print:hidden">
      {showTop && (
        <Button
          variant="secondary"
          size="icon"
          className="h-10 w-10 rounded-full shadow-md opacity-80 hover:opacity-100 transition-opacity bg-background/80 backdrop-blur-sm border"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      )}
      {showBottom && (
        <Button
          variant="secondary"
          size="icon"
          className="h-10 w-10 rounded-full shadow-md opacity-80 hover:opacity-100 transition-opacity bg-background/80 backdrop-blur-sm border"
          onClick={scrollToBottom}
          aria-label="Scroll to bottom"
        >
          <ArrowDown className="h-5 w-5" />
        </Button>
      )}
    </div>
  )
}
